import React, { useEffect, useRef, useState } from "react";

import type { CreateMeetingRequestJson, ExtendedWorkspace } from "@performyard/service-contracts";

import { CalendarErrorModal } from "./CalendarErrorModal";
import MeetingForm, { type MeetingFormData } from "./MeetingForm";
import { VideoConferencingErrorModal } from "./VideoConferencingErrorModal";

import { useAddWorkspaceMeeting } from "../../../../../api/hooks";
import useForm from "../../../../../hooks/useForm";
import { useCalendarErrorHandler, useValidationErrorClearer, useVideoConferencingErrorHandler } from "./hooks";

import { type MeetingFormSchema, meetingFormSchema } from "./meeting-form-schema";
import { type MeetingFormValuesWithOptionalDates, getDefaultFormValues } from "./meeting-form-utils";

type CreateMeetingModalProps = {
	isOpen: boolean;
	handleClose: () => void;
	workspace: ExtendedWorkspace;
	workspaceHasMeetings: boolean;
	setExpandedMeetingIds?: React.Dispatch<React.SetStateAction<string[]>>;
};

export const CreateMeetingModal = ({
	isOpen,
	handleClose,
	workspace,
	workspaceHasMeetings,
	setExpandedMeetingIds = () => {},
}: CreateMeetingModalProps): React.ReactElement => {
	const [validationErrors, setValidationErrors] = useState<Partial<Record<keyof MeetingFormSchema, string>>>({});

	const { modalState: calendarModalState, handleCalendarError, closeModal, setRetrying } = useCalendarErrorHandler();
	const {
		modalState: vcModalState,
		handleVideoConferencingError,
		closeModal: closeVcModal,
		setRetrying: setVcRetrying,
	} = useVideoConferencingErrorHandler();

	const { mutate: addWorkspaceMeeting, isPending } = useAddWorkspaceMeeting(
		handleClose,
		setExpandedMeetingIds,
		(error, variables) => {
			const context = {
				operation: "create" as const,
				meetingData: variables.data,
				workspaceId: variables.workspaceId,
			};
			// Try VC first so the meeting-creation flow surfaces a VC-specific modal
			// before falling back to the calendar one.
			if (handleVideoConferencingError(error, context)) return true;
			return handleCalendarError(error, context);
		}
	);

	const {
		validate,
		updateForm,
		state: formValues,
		setNewDefaultValues,
	} = useForm({
		schema: meetingFormSchema,
		defaultValues: getDefaultFormValues(),
		disableWarning: true,
	});

	const hasInitializedRef = useRef(false);

	useEffect(() => {
		if (isOpen && !hasInitializedRef.current) {
			setNewDefaultValues({ ...getDefaultFormValues(), workspaceId: workspace._id });
			setValidationErrors({});
			hasInitializedRef.current = true;
		} else if (!isOpen) {
			hasInitializedRef.current = false;
		}
	}, [isOpen, setNewDefaultValues, workspace._id]);

	// Cast to optional dates type since the form allows clearing dates during editing
	const formValuesWithOptionalDates = formValues as MeetingFormValuesWithOptionalDates;

	useValidationErrorClearer(formValuesWithOptionalDates, validationErrors, setValidationErrors);

	const handleSubmit = (data: MeetingFormData): void => {
		const validationResult = validate();
		if (!validationResult.isValid) {
			const errors = Object.entries(validationResult.errors || {}).reduce(
				(acc, [key, error]) => {
					acc[key as keyof MeetingFormSchema] = error.message;
					return acc;
				},
				{} as Partial<Record<keyof MeetingFormSchema, string>>
			);
			setValidationErrors(errors);
			return;
		}

		const meetingParticipants = workspace.participants?.map((participant) => ({
			userId: participant.userId,
			role: participant.role,
		}));

		const formattedData: CreateMeetingRequestJson = {
			...data,
			startDateTime: data.startDateTime.toISOString(),
			endDateTime: data.endDateTime.toISOString(),
			participants: meetingParticipants,
			// The form holds the chosen provider (null = none); omit it from the request when none.
			videoConferencingProvider: data.videoConferencingProvider ?? undefined,
		};

		addWorkspaceMeeting({ workspaceId: workspace._id, data: formattedData });
	};

	const onClose = (): void => {
		if (calendarModalState.type !== "none") {
			closeModal();
			return;
		}
		if (vcModalState.type !== "none") {
			closeVcModal();
			return;
		}

		handleClose();
		setValidationErrors({});
	};

	return (
		<>
			{calendarModalState.type === "none" && vcModalState.type === "none" && (
				<MeetingForm
					isOpen={isOpen}
					onClose={onClose}
					onSubmit={handleSubmit}
					formValues={formValuesWithOptionalDates}
					onFormChange={updateForm}
					validationErrors={validationErrors}
					submitLabel="Create Meeting"
					isPending={isPending}
					title="Create a New Meeting"
					showCarryOver={workspaceHasMeetings}
					showMeetingIntegrations={true}
				/>
			)}

			<VideoConferencingErrorModal
				modalState={vcModalState}
				onRetry={() => {
					if (vcModalState.type === "error") {
						setVcRetrying(true);
						const { context } = vcModalState;
						if (context.operation === "create") {
							addWorkspaceMeeting(
								{ workspaceId: context.workspaceId, data: context.meetingData },
								{
									onSuccess: () => closeVcModal(),
									onError: () => setVcRetrying(false),
								}
							);
						}
					}
				}}
				onContinue={() => {
					if (vcModalState.type === "error") {
						const { context } = vcModalState;
						if (context.operation === "create") {
							setVcRetrying(true);
							addWorkspaceMeeting(
								{
									workspaceId: context.workspaceId,
									data: { ...context.meetingData, videoConferencingProvider: undefined },
								},
								{
									onSuccess: () => closeVcModal(),
									onError: () => setVcRetrying(false),
								}
							);
						}
					}
				}}
				onClose={closeVcModal}
			/>

			<CalendarErrorModal
				modalState={calendarModalState}
				onRetry={() => {
					if (calendarModalState.type === "error") {
						setRetrying(true);
						const { context } = calendarModalState;
						if (context.operation === "create") {
							addWorkspaceMeeting(
								{ workspaceId: context.workspaceId, data: context.meetingData },
								{
									onSuccess: () => closeModal(),
									onError: () => setRetrying(false),
								}
							);
						}
					}
				}}
				onContinue={() => {
					if (calendarModalState.type === "error") {
						const { context } = calendarModalState;
						if (context.operation === "create") {
							const dataWithoutCalendar = { ...context.meetingData, addToCalendar: false };

							setRetrying(true);
							addWorkspaceMeeting(
								{ workspaceId: context.workspaceId, data: dataWithoutCalendar },
								{
									onSuccess: () => closeModal(),
									onError: () => setRetrying(false),
								}
							);
						}
					}
				}}
				onClose={closeModal}
			/>
		</>
	);
};
